import React, { useContext, useEffect, useState } from 'react'
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import MultiSelectEntity from '../RefineSearch/MultiSelectEntity';
import Popup from '../Common/Popup';
import useModule from '../../hooks/useModule';

/**
 * The CityFilter function is a component in JavaScript that renders a filter for selecting cities and
 * updates the selected city options based on user input.
 * @returns The CityFilter component is returning a JSX element.
 */
function CityFilter({ customPathname, setCityOptions, CityOptions, label = "City (of Target Company)", isPopupOpen, setIsPopupOpen }) {
    const { Cities, refineSearchFilters, accessControl } = useContext(PeFilterContext);
    const { isPE, isVC } = useModule();
    
    // Use internal state only if props are not provided (for backward compatibility)
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

   /* The `useEffect` hook is used to perform side effects in a functional component. In this case, the
   `useEffect` hook is used to update the selected city options based on user input. */
    useEffect(() => {
        const ids = refineSearchFilters[customPathname]?.city?.map(value => value.id) ?? [];
        setCityOptions(Cities?.map(option => ({ ...option, isChecked: ids.includes(option.id) })))
    }, [Cities, customPathname, refineSearchFilters, setCityOptions])


    /* The `handleCityChange` function is an event handler that is triggered when the user selects or
    deselects a city option in the filter. It receives the event object `e` as a parameter. */
    const handleCityChange = (e) => {
        const { name, checked, id, filteredIds } = e.target;
        let currentCities = [];
    
        if (name === "allSelect") {
            currentCities = CityOptions.map((city) =>
                ({ ...city, isChecked: checked })
            );
        } else if (name === "filteredSelect" && filteredIds) {
            currentCities = CityOptions.map((city) => {
                if (filteredIds.includes(city.id)) {
                    return { ...city, isChecked: checked };
                }
                return city;
            });
        } else {
            currentCities = CityOptions.map((city) =>
                city.id === parseInt(id) ? { ...city, isChecked: checked } : city
            );
        }
    
        setCityOptions(currentCities);
    };
    

    const len = CityOptions?.filter(option => option.isChecked)?.length ?? 0
    const enabledCities = accessControl ? isPE || isVC ? accessControl?.pe_cities?.id : accessControl?.ma_cities?.id : [];

    const disabledCities = enabledCities?.length > 0 ? CityOptions?.filter(option => !enabledCities.includes(option.id)) : [];

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100  text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label} <span className="opacity-60 ml-2 text-xs text-ellipsis">e.g. Chennai, Bangalore</span></span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {len > 0 && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {len}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    <div className="p-4">
                        <MultiSelectEntity
                            handleEntityChange={handleCityChange}
                            primaryEntity={CityOptions}
                            disabled={disabledCities}
                        />
                    </div>
                </Popup>
            )}
        </div>
    )
}

export default CityFilter